/* Which Ledger preferences a device holds after signing in, and what of them
 * may go up to the account.
 *
 * The prefs are a statement, so the newer copy wins wholesale — except the
 * username, which is not just a preference: it is the only thing telling a
 * second device which cloud cache (users/{uid}/ledgerCaches/{username}) holds
 * this account's games. An EMPTY username is the absence of a choice, not a
 * choice, and it used to win: a fresh device stored empty prefs stamped with
 * the time it first mounted, which is later than the desktop's real ones, so
 * signing in kept the empty name, found no cache, showed an empty Ledger — and
 * then pushed the empty name up over the real one (Android, 2026-10-22).
 *
 * The username travels with its site: "piskunow" on Lichess and "piskunow" on
 * Chess.com are different caches. */
const named = (prefs) => Boolean((prefs?.username || "").trim());

/* `local` is this device's stored prefs (carrying its own updatedAt); `remote`
 * is the account's document, { prefs, updatedAt }, or null. Returns `local`
 * itself when nothing should change, so callers can tell. */
export function resolveLedgerPrefs(local, remote) {
  const remotePrefs = remote?.prefs;
  if (!remotePrefs) return local;
  const remoteNewer = (remote.updatedAt || 0) > (local.updatedAt || 0);
  const winner = remoteNewer ? remotePrefs : local;
  const other = remoteNewer ? local : remotePrefs;
  return !named(winner) && named(other) ? { ...winner, username: other.username, site: other.site } : winner;
}

/* The write boundary. The cloud write merges (ledgerSync.js), so a field left
 * out is a field left alone: an unnamed device can still share its filters
 * without erasing the name another device chose. A read that failed reads as
 * "no remote prefs", which is exactly when an unguarded push would do that. */
export function prefsForCloud(prefs) {
  if (named(prefs)) return prefs;
  const rest = { ...prefs };
  delete rest.username;
  delete rest.site;
  return rest;
}
